/**
 * FAQFilters Component
 *
 * Handles search and category filtering for FAQs
 * Extracted from FAQs.tsx as part of Phase 3 decomposition
 *
 * Responsibilities:
 * - Search input for filtering FAQs by question/answer/tags
 * - Category filter chips
 * - Filter toggle visibility
 * - Display filtered/total counts
 */

import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface FAQFiltersProps {
  /** Current search query */
  searchQuery: string;
  /** Callback when search query changes */
  onSearchChange: (query: string) => void;
  /** Currently selected category (empty string for all) */
  selectedCategory: string;
  /** Callback when category selection changes */
  onCategoryChange: (category: string) => void;
  /** Available categories to filter by */
  categories: string[];
  /** Current sort option */
  sortBy: string;
  /** Callback when sort option changes */
  onSortChange: (sortBy: string) => void;
  /** Currently selected approval status (empty string for all) */
  approvalStatus?: string;
  /** Callback when the status selection changes */
  onApprovalStatusChange?: (status: string) => void;
  /** Whether category filters are visible */
  showFilters: boolean;
  /** Callback to toggle filter visibility */
  onToggleFilters: () => void;
}

/**
 * FAQFilters - Search and category filtering UI
 *
 * Performance: Wrapped in React.memo to prevent unnecessary re-renders
 *
 * @example
 * ```tsx
 * <FAQFilters
 *   searchQuery={searchQuery}
 *   onSearchChange={setSearchQuery}
 *   selectedCategory={selectedCategory}
 *   onCategoryChange={setSelectedCategory}
 *   categories={categories}
 *   showFilters={showFilters}
 *   onToggleFilters={() => setShowFilters(!showFilters)}
 * />
 * ```
 */
export const FAQFilters = React.memo<FAQFiltersProps>(({
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  categories,
  sortBy,
  onSortChange,
  approvalStatus = '',
  onApprovalStatusChange,
  showFilters,
  onToggleFilters
}) => {
  const { t } = useTranslation();

  /**
   * Handle search input change
   * Wrapped in useCallback for stable reference
   */
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(event.target.value);
  }, [onSearchChange]);

  /**
   * Handle sort selection change
   * Wrapped in useCallback for stable reference
   */
  const handleSortChange = useCallback((event: SelectChangeEvent<string>) => {
    onSortChange(event.target.value);
  }, [onSortChange]);

  /**
   * Handle category selection
   * Wrapped in useCallback for stable reference
   */
  const handleCategoryClick = useCallback((category: string) => {
    onCategoryChange(category);
  }, [onCategoryChange]);

  /**
   * Handle "All Categories" selection
   * Wrapped in useCallback for stable reference
   */
  const handleAllCategoriesClick = useCallback(() => {
    onCategoryChange('');
  }, [onCategoryChange]);

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        {/* Search Input */}
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label={t('faq.search')}
            placeholder={t('faq.search_placeholder')}
            value={searchQuery}
            onChange={handleSearchChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Grid>

        {/* Statusfilter: seit die Kacheln im Moderationsreiter filtern, fehlte
            im Reiter "Alle FAQs" jede Moeglichkeit, nach Status einzugrenzen.
            Nur sichtbar, wenn ein Empfaenger dafuer da ist - ein Nutzer ohne
            Moderationsrechte sieht ohnehin nur genehmigte FAQs. */}
        {onApprovalStatusChange ? (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="faq-status-label">{t('faq.filters.statusLabel')}</InputLabel>
              <Select
                labelId="faq-status-label"
                value={approvalStatus}
                label={t('faq.filters.statusLabel')}
                onChange={(event) => { onApprovalStatusChange(event.target.value); }}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">{t('faq.filters.statusAll')}</MenuItem>
                <MenuItem value="approved">{t('faq.approvalStatus.approved')}</MenuItem>
                <MenuItem value="pending_review">{t('faq.approvalStatus.pending_review')}</MenuItem>
                <MenuItem value="rejected">{t('faq.approvalStatus.rejected')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        ) : null}

        {/* Sort Dropdown */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="faq-sort-label">{t('faq.sorting.label')}</InputLabel>
            <Select
              labelId="faq-sort-label"
              value={sortBy}
              label={t('faq.sorting.label')}
              onChange={handleSortChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="newest">{t('faq.sorting.newest')}</MenuItem>
              <MenuItem value="oldest">{t('faq.sorting.oldest')}</MenuItem>
              <MenuItem value="best_rated">{t('faq.sorting.bestRated')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Filter Toggle - only when there is something to filter by. A button
            that is visible but does nothing is worse than no button: the reader
            clicks it, nothing happens, and they distrust the next control too.
            The count is gone with it; the cards above already show it, and a
            second number in a second place only invites comparing the two. */}
        {categories.length > 0 && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <IconButton
                onClick={onToggleFilters}
                color={showFilters ? 'primary' : 'default'}
                aria-label={showFilters ? t('faq.filters.hide') : t('faq.filters.show')}
              >
                <FilterIcon />
              </IconButton>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Category Filters */}
      {showFilters && categories.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('faq.list.categories')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {/* All Categories Chip */}
            <Chip
              label={t('faq.list.allCategories')}
              onClick={handleAllCategoriesClick}
              variant={selectedCategory === '' ? 'filled' : 'outlined'}
              color="primary"
              clickable
            />

            {/* Category Chips */}
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => handleCategoryClick(category)}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                color="secondary"
                clickable
              />
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
});

FAQFilters.displayName = 'FAQFilters';
